import { useId } from "react";
import "../styles/components/RegistrationForm.css";

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <div className="form__error" id={id} role="alert">
      {message}
    </div>
  );
}

export default function RegistrationForm({
  form,
  errors,
  dinnerOptions,
  isSubmitting,
  submitError,
  onChange,
  onSubmit,
}) {
  const dinnerLegendId = useId();
  const termsErrorId = useId();
  const submitErrorId = useId();

  return (
    <section className="register" aria-label="Registration">
      <div className="container register__inner">
        <div className="register__left">
          <h2 className="register__heading">REGISTER NOW</h2>
          <p className="register__lede">
            {/* Secure your spot at the
            <br />
            premier innovation
            <br />
            conference of 2026 */}
            <i>
              Join MHUSA and distributor partners in Chicago as we shape what’s next—together.
            </i>
          </p>
        </div>

        <form className="form" onSubmit={onSubmit} noValidate>
          {submitError ? (
            <div className="form__submitError" id={submitErrorId} role="alert">
              {submitError}
            </div>
          ) : null}

          <div className="form__grid">
            <div className="form__field">
              <label className="form__label" htmlFor="firstName">
                First Name<span aria-hidden="true">*</span>
              </label>
              <input
                className="form__input"
                id="firstName"
                name="firstName"
                type="text"
                placeholder="John"
                value={form.firstName}
                onChange={(e) => onChange("firstName", e.target.value)}
                aria-invalid={Boolean(errors.firstName)}
                aria-describedby={errors.firstName ? "firstName-error" : undefined}
                disabled={isSubmitting}
                required
              />
              <FieldError id="firstName-error" message={errors.firstName} />
            </div>

            <div className="form__field">
              <label className="form__label" htmlFor="lastName">
                Last Name<span aria-hidden="true">*</span>
              </label>
              <input
                className="form__input"
                id="lastName"
                name="lastName"
                type="text"
                placeholder="Doe"
                value={form.lastName}
                onChange={(e) => onChange("lastName", e.target.value)}
                aria-invalid={Boolean(errors.lastName)}
                aria-describedby={errors.lastName ? "lastName-error" : undefined}
                disabled={isSubmitting}
                required
              />
              <FieldError id="lastName-error" message={errors.lastName} />
            </div>

            <div className="form__field form__field--full">
              <label className="form__label" htmlFor="email">
                Email Address<span aria-hidden="true">*</span>
              </label>
              <input
                className="form__input"
                id="email"
                name="email"
                type="email"
                placeholder="john.doe@company.com"
                autoComplete="email"
                value={form.email}
                onChange={(e) => onChange("email", e.target.value)}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? "email-error" : undefined}
                disabled={isSubmitting}
                required
              />
              <FieldError id="email-error" message={errors.email} />
            </div>

            <div className="form__field form__field--full">
              <label className="form__label" htmlFor="company">
                Company Name<span aria-hidden="true">*</span>
              </label>
              <input
                className="form__input"
                id="company"
                name="company"
                type="text"
                placeholder="Good Company"
                value={form.company}
                onChange={(e) => onChange("company", e.target.value)}
                aria-invalid={Boolean(errors.company)}
                aria-describedby={errors.company ? "company-error" : undefined}
                disabled={isSubmitting}
                required
              />
              <FieldError id="company-error" message={errors.company} />
            </div>

            <div className="form__field form__field--full">
              <fieldset className="form__fieldset" aria-labelledby={dinnerLegendId} disabled={isSubmitting}>
                <legend className="form__legend" id={dinnerLegendId}>
                  Wednesday Dinner Selection<span aria-hidden="true">*</span>
                </legend>

                <div className="form__choices" role="radiogroup" aria-label="Wednesday Dinner Selection">
                  {Object.entries(dinnerOptions).map(([key, option]) => (
                    <label className="form__choice" key={key}>
                      <input
                        className="form__radio"
                        type="radio"
                        name="dinnerSelection"
                        value={key}
                        checked={form.dinnerSelection === key}
                        onChange={() => onChange("dinnerSelection", key)}
                        disabled={isSubmitting}
                        required
                      />
                      <span className="form__choiceContent">
                        <span className="form__choiceTitle">{option.title}</span>
                        <span className="form__choiceDesc">{option.description}</span>
                      </span>
                    </label>
                  ))}
                </div>

                <FieldError id="dinnerSelection-error" message={errors.dinnerSelection} />
              </fieldset>
            </div>

            <div className="form__field form__field--full">
              <label className="form__label" htmlFor="dietaryRestrictions">
                Please let us know if you have any allergies or

                dietary restrictions<span aria-hidden="true">*</span>
              </label>
              <textarea
                className="form__textarea"
                id="dietaryRestrictions"
                name="dietaryRestrictions"
                placeholder="Vegetarian, vegan, allergies, etc."
                value={form.dietaryRestrictions}
                onChange={(e) => onChange("dietaryRestrictions", e.target.value)}
                aria-invalid={Boolean(errors.dietaryRestrictions)}
                aria-describedby={errors.dietaryRestrictions ? "dietaryRestrictions-error" : undefined}
                disabled={isSubmitting}
                required
              />
              <FieldError id="dietaryRestrictions-error" message={errors.dietaryRestrictions} />
            </div>
          </div>

          <p className="form__disclaimer">
            Please note this form is not your hotel registration. A hotel registration link will be
            provided once the form is submitted.
          </p>

          <label className="form__terms">
            <input
              className="form__checkbox"
              type="checkbox"
              checked={form.termsAccepted}
              onChange={(e) => onChange("termsAccepted", e.target.checked)}
              aria-invalid={Boolean(errors.termsAccepted)}
              aria-describedby={errors.termsAccepted ? termsErrorId : undefined}
              disabled={isSubmitting}
              required
            />
            <span>
              I agree to the{" "}
              <a href="https://www1.mhusa.com/Help/privacy-policy.html" target="_blank" rel="noopener noreferrer">
                Terms and Conditions.
              </a>
            </span>
          </label>
          <FieldError id={termsErrorId} message={errors.termsAccepted} />

          <div className="form__actions">
            <button className="form__submit" type="submit" disabled={isSubmitting} aria-busy={isSubmitting}>
              {isSubmitting ? "SUBMITTING..." : "SUBMIT"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
